/* -----------------------------------------------------------------------------------------------------
  - Created: [13-02-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains equipment list table
  -------------------------------------------------------------------------------------------------------
*/

import React, { useEffect, useState } from 'react';
import EquipmentSearchFilter from '../EquipmentSearchFilter/EquipmentSearchFilter';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import EquipmentForm from '../EquipmentForm/EquipmentForm';
import { equipment_creation, equipment_update, get_equipment_by_id, get_equipment_with_pagination } from '../../../../config/Resource/equipmentApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import { useSelector } from 'react-redux';
import { hasPrivilege } from '../../../../utils/privileges';
import Loader from '../../../../Components/Loader/Loader';
import { Icons } from '../../../../utils/GlobalIcons';
import './EquipmentTableStyle.css';
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';
import { fetchHistoryData } from '../../../../config/HistoryService/historyService';


function EquipmentTable() {

    const privileges = useSelector((state) => state.privileges.privileges);

    // State variables for managing equipment data, pagination, and modal states
    const [equipmentListing, setEquipmentListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
    const [refresh, setRefresh] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });
    const [filterValues, setFilterValues] = useState({});
    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [showHistory, setShowHistory] = useState(false); // history modal state
    const [historyData, setHistoryData] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false); // Loader state


    // Fetch equipment data on page change or refresh
    useEffect(() => {
        fetchEquipmentData();
    }, [pageNumber, totalItemPerPage, refresh, filterValues]);

    // Fetch equipment data from API
    const fetchEquipmentData = async () => {

        try {
            setIsLoading(true)
            const formData = new FormData();
            formData.append('page', pageNumber);
            formData.append('pageItemcount', totalItemPerPage);
            // Append filter values to formData
            Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));
            const response = await get_equipment_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                setEquipmentListing(data.data);
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));
            } else {
                setEquipmentListing([]);
            }
        } catch (error) {
            setEquipmentListing([]);
        } finally {
            setIsLoading(false)
        }
    };

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Resources", 5, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Resources", 5, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Resources", 5, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Resources", 5, PERMISSION_ACTIONS.add),
    };
    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;


    // Columns for the table
    const columns = [
        { title: "#", key: "key" },
        { title: "Equipment", key: "equipment" },
        { title: "Short Code", key: "equipmentShortCode" },
        { title: "Serial No", key: "SerialNo" },
        { title: "Manufacturer", key: "Manufacturer" },
        { title: "Year of Purchase", key: "purchaseYear" },
        { title: "Expiry Date", key: "expiryDate" },
        { title: "Technology", key: "Technology" },
        { title: "Current Status", key: "CurrentStatus" },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.status === 1 ? 'Active' : 'Inactive';
                const style = getStatusColors(record?.status);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },

        { title: "Actions", key: "actions", render: (text, row) => renderActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false

    // Render action icons for each project item
    const renderActions = (row) => (
        <div className="action-container">
            {userPermissions.view &&
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => handleModal(true, row?.id, 'view')} />
                </CustomTooltipWithoutRoundBorder>
            }
            {userPermissions.edit &&
                <CustomTooltipWithoutRoundBorder title="Edit">
                    <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>

            }
            {userPermissions.history &&
                <CustomTooltipWithoutRoundBorder title="History">
                    <Icons.History className="history-action-icon" onClick={() => handleHistoryModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>}
        </div>
    );


    // Map the equipment data into table rows
    const rows = () => equipmentListing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.EquipmentId,
        equipment: item.EquipmentName,
        equipmentShortCode: item.EquipmentShortcode,
        status: item?.EquipmentStatus,
        expiryDate: item?.EquipmentExpiry,
        purchaseYear: item?.EquipmentPurchaseYear,
        SerialNo: item?.EquipmentserialNo,
        Manufacturer: item?.Name_of_Manufacturer,
        CurrentStatus: item?.CurrentStatus,
        Technology: item?.Technologies?.map((tech) => tech?.TechnologyName).join(', ') || '',

    }));

    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // Open Add/Edit/View modal
    const handleModal = async (isOpen, equipmentId, actionType) => {
        if (actionType === 'view' && equipmentId) { //FOR VIEW
            await fetchEquipmentDetails(equipmentId,);
            setIsViewModalOpen(true);
        } else if (equipmentId) { //FOR EDIT
            await fetchEquipmentDetails(equipmentId);
        } else { //FOR ADD
            setModalProps({
                isOpen,
                id: '',
                formValues: { // Set initial values  props
                    EquipmentName: '',
                    EquipmentShortcode: '',
                    EquipmentserialNo: '',
                    EquipmentCalibrationDate: '',
                    technology_ids: [],
                    EquipmentPurchaseYear: '',
                    EquipmentDescription: '',
                    EquipmentStatus: 1,
                    EquipmentExpiry: '',
                    Name_of_Manufacturer: ''
                }
            });
        }

    };

    // Fetch details of a specific equipment item
    const fetchEquipmentDetails = async (equipmentId, actionType) => {
        try {
            const response = await get_equipment_by_id({ equipmentId });
            if (response?.status === "success") {
                const data = response?.data;
                setModalProps({
                    isOpen: actionType === 'view' ? false : true,
                    id: data?.EquipmentId,
                    formValues: {
                        EquipmentName: data?.EquipmentName,
                        EquipmentShortcode: data?.EquipmentShortcode,
                        EquipmentserialNo: data?.EquipmentserialNo,
                        EquipmentCalibrationDate: data?.EquipmentCalibrationDate,
                        technology_ids: actionType === 'view' ? data?.technologies || [] : data?.technologies?.map((item) => item?.TechnologiesTypeId) || [],
                        EquipmentPurchaseYear: data?.EquipmentPurchaseYear,
                        EquipmentDescription: data?.EquipmentDescription,
                        EquipmentStatus: data?.EquipmentStatus,
                        EquipmentExpiry: data?.EquipmentExpiry,
                        Name_of_Manufacturer: data?.Name_of_Manufacturer,
                    },
                });
            } else {
                ErrorToast(response?.message || 'Something went wrong!');
            }
        } catch (error) {
            ErrorToast('Something went wrong!');
        }
    };

    // Handle saving equipment data (add/edit)
    const handleEquipmentSave = async (values, equipmentId) => {
        try {
            setIsButtonDisabled(true);
            const apiFunction = equipmentId ? equipment_update : equipment_creation;
            if (equipmentId) values.equipmentId = equipmentId;

            const response = await apiFunction(values);
            const message = response?.data?.message || 'Something went wrong!';
            if (response?.data?.status === "success") {
                SuccessToast(message);
                closeModal();
                setRefresh(!refresh);
            } else {
                ErrorToast(message);
            }
        } catch (error) {
            const errorMessage = error?.data?.errors ? Object.values(error.data.errors)[0][0] : 'Something went wrong!';
            ErrorToast(errorMessage);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setIsViewModalOpen(false);
    };


    // Handle pagination changes
    const handlePageChange = (pageNumber) => setPageNumber(pageNumber);
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);


    // HISTORY BUTTON CLICK AND API CALL
    const handleHistoryModal = async (open, id) => {
        setShowHistory(open);

        if (open) {
            setHistoryLoader(true);
            try {
                const response = await fetchHistoryData('equipment', id);

                if (response?.status === "success") {

                    //  Transform API response for  table usage
                    const formatted = response?.data?.map((item, index) => ({
                        id: item.id,
                        name: item.equipment_name || "-",
                        changedBy: item.changed_by_name || "Unknown",
                        changedAt: item.changed_at || "-",
                        changeDescription: item.change_description || "-",
                    }));

                    setHistoryData(formatted || []);
                } else {
                    // InfoToast("No history data found.");
                    setHistoryData([]);
                }
            } catch (error) {
                console.error("Error fetching project history:", error);
            } finally {
                setHistoryLoader(false);
            }
        } else {
            setHistoryData([]);
        }
    };


    return (
        <div className='equipment-table-container'>
            <EquipmentSearchFilter
                searchFilter={handleSearchFilter}
                tableTitle="Equipment"
                isAddEditModalOpen={modalProps.isOpen}
                handleAddEditModal={handleModal}
                isAddPermission={userPermissions?.add}

                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}

            />

            <CustomTable columns={columns} rows={isLoading ? [] : rows()} renderActions={renderActions} isLoading={isLoading} />

            {isLoading ? <Loader /> : <CustomBottomPagination
                items_per_page={totalItemPerPage}
                current_page={pageNumber}
                total_page_count={totalPages}
                on_change_value={handlePageChange}
                on_page_count_update={handleItemPageCountChange}
                pagination={pagination}
            />
            }

            {/* ADD/EDIT/VIEW MODAL */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={closeModal}
                title={isViewModalOpen ? "View Equipment" : modalProps.id ? "Edit Equipment" : "Create Equipment"}
                body={<EquipmentForm
                    isDisabled={isViewModalOpen}
                    isButtonDisabled={isButtonDisabled}
                    id={modalProps.id}
                    formValues={modalProps.formValues}
                    handleSave={handleEquipmentSave}
                />}
                maxWidth="800px"
            />}


            {/* HISTORY VIEW MODAL */}
            {showHistory && <CustomModal
                open={showHistory}
                handleClose={() => handleHistoryModal(false)}
                title="Equipment History"
                maxWidth="75%"
                body={
                    historyLoader ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Loader />
                        </div>
                    ) : (
                        <ActionHistory
                            title=""
                            data={historyData}
                            columns={[
                                { key: "name", label: "Equipment" },
                                { key: "changedBy", label: "Changed By" },
                                { key: "changedAt", label: "Changed At" },
                                { key: "changeDescription", label: "Change Description" },
                            ]}
                            noDataMessage="No change history available."
                        />

                    )
                }
            />}
        </div>
    );
}

export default EquipmentTable;
